import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Alert, AlertDescription } from '../../ui/alert';
import { Label } from '../../ui/label';
import { Loader2, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { useReportData } from '../../../hooks/useReportData';
import { useExcelExport } from '../../../hooks/useExcelExport';
import { userService } from '../../../_services';

/**
 * Tier Report Dialog
 * Modernized modal for downloading tier reports
 * Supports: User Tier Report, Minimum Payout Report
 */
function TierReportDialog({ open, onOpenChange, title }) {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Determine report type
    const isUserTier = title === 'User Tier';
    const isMinimumPayout = title === 'Minimum Payout';

    // Fetch month list only when dialog is open
    const reportType = isUserTier ? 'userTierMonths' : isMinimumPayout ? 'minimumPayoutMonths' : null;
    const { data: monthData, loading: loadingMonths } = useReportData(reportType, {}, open);

    const { exportToExcel } = useExcelExport();

    // Set default month when data loads
    useEffect(() => {
        if (monthData && monthData.length > 0 && !selectedMonth) {
            const firstMonth = isMinimumPayout ? monthData[0].monthname : monthData[0].monthvalue;
            setSelectedMonth(firstMonth);
        }
    }, [monthData, selectedMonth, isMinimumPayout]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setError('');
            setDownloading(false);
        }
    }, [open]);

    // Download report handler
    const handleDownload = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setDownloading(true);
        setError('');

        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const userId = usermetaData.split(':')[0];
            const userName = usermetaData.split(':')[1];
            const userRole = window.atob(localStorage.getItem('UserRole'));
            
            if (isUserTier) {
                // Parse month and year from selectedMonth (format: "monthvalue")
                const monthYear = selectedMonth; // Should be in format like "2024-01"
                const [year, month] = monthYear.split('-');
                
                // Download User Tier Report as Excel
                const response = await userService.GetUserTierReport(userId, userName, userRole, month, year);
                
                if (!response || response.length === 0) {
                    setError('No data available for the selected month');
                    setDownloading(false);
                    return;
                }

                // Transform data for Excel
                const excelData = response.map(item => ({
                    'Group Number': item.groupNumber || '',
                    'Affiliate Name': item.affiliateName || '',
                    'Parent Affiliate Name': item.parent_Affiliate_Name || '',
                    'Number of Paid Transactions': item.number_of_Paid_Transactions || 0,
                    'Rank/Tier': item.tierName || ''
                }));

                // Export to Excel
                await exportToExcel(excelData, 'User_Tier_Report', {
                    sheetName: 'User Tier Report'
                });

                // Close dialog on success
                setTimeout(() => {
                    onOpenChange(false);
                }, 500);

            } else if (isMinimumPayout) {
                // Open external reporting URL for Minimum Payout
                const currentUrl = window.location.href;
                const isDevEnvironment = currentUrl.toLowerCase().includes("localhost:") || 
                                       currentUrl.toLowerCase().includes("dev-backoffice");

                const baseUrl = isDevEnvironment 
                    ? 'https://dev.glichealth.com'
                    : 'https://reporting.glichealth.com';
                
                const objectId = isDevEnvironment 
                    ? 'B8B95710360E4BCB'
                    : '772BB34B1E0E4BA1';

                const reportUrl = `${baseUrl}/report/payout_report_jd_IsPayable?&name=patient%20rx%20discount&wh_id=61&csvfor=ds&m=${selectedMonth}&processor=0&ObjectId=${objectId}`;

                window.open(reportUrl, '_blank');

                // Close dialog after opening URL
                setTimeout(() => {
                    onOpenChange(false);
                }, 500);
            }

        } catch (err) {
            console.error('Error downloading report:', err);
            setError('Failed to download report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-brand-dark">
                        {title} Report
                    </DialogTitle>
                    <DialogDescription>
                        {isUserTier 
                            ? 'Select a month to download the tier report as Excel'
                            : 'Select a month to view the minimum payout report'
                        }
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Month Selector */}
                    <div className="space-y-2">
                        <Label htmlFor="month-select">Choose Date</Label>
                        {loadingMonths ? (
                            <div className="flex items-center gap-2 text-brand-dark/70">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading months...</span>
                            </div>
                        ) : monthData && monthData.length > 0 ? (
                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger id="month-select" className="w-full">
                                    <SelectValue placeholder="Select Month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {isMinimumPayout ? (
                                        monthData.map((item, index) => (
                                            <SelectItem key={index} value={item.monthname}>
                                                {item.monthname}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        monthData.map((item, index) => (
                                            <SelectItem key={index} value={item.monthvalue}>
                                                {item.monthname}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    No months available. Please try again later.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={downloading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={downloading || loadingMonths || !selectedMonth}
                        variant="secondary"
                    >
                        {downloading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {isUserTier ? 'Downloading...' : 'Opening...'}
                            </>
                        ) : (
                            <>
                                {isUserTier ? (
                                    <>
                                        <Download className="mr-2 h-4 w-4" />
                                        Download
                                    </>
                                ) : (
                                    <>
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        View Report
                                    </>
                                )}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TierReportDialog;
